import { Injectable, inject } from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {environment} from '@environments/environment';

/** A scheduled launch's registration platform syncs, by install. */
export interface LaunchSyncOptions {
  /** The install synced before the launch. */
  sync_project_registration_platform_id?: number | null;
  /** Sync once the campaign has launched, to push the share links it created. */
  sync_influencers_after_launch?: boolean;
  /** The install synced after the launch: one whose syncs push share links. */
  sync_after_launch_registration_platform_id?: number | null;
}

@Injectable({
  providedIn: 'root'
})

export class CampaignService {
  private http = inject(HttpClient);

  private baseApiUrl = environment.apiUrl;
  private campaignUrl = this.baseApiUrl + '/api/v1/campaign';
  private shareUrl = this.baseApiUrl + '/api/v1/share';

  getCampaign(id: number, params: any = {}) {
    return this.http.get(`${this.campaignUrl}/${id}`, {params});
  }

  updateCampaign(campaign, id) {
    campaign = {...campaign};
    if (campaign.project_id) {
      delete campaign.project_id;
    }
    return this.http.put(`${this.campaignUrl}/${id}`, campaign);
  }

  addCampaign(campaign) {
    return this.http.post(`${this.campaignUrl}`, campaign);
  }

  getCampaigns(filters = {}) {
    return this.http.get(`${this.campaignUrl}`, {params: filters, headers: new HttpHeaders().set('Use-Temp-Token', 'true')});
  }

  getLaunchCode(campId) {
    return this.http.post(`${this.campaignUrl}/${campId}/launch/code`, {});
  }

  launch(campId, code, reLaunch = false) {
    return this.http.post(`${this.campaignUrl}/${campId}/launch`, { launch_code: code, ...(reLaunch ? {reLaunch} : {})});
  }

  launchValidation(params) {
    return this.http.get(`${this.campaignUrl}/${params.campaign_id}/launch/validation`, {params});
  }

  /**
   * `syncOptions`: the install synced before the launch (`sync_registration_platform` stays its slug,
   * for an older server), and the sync after the launch on an install pushing share links.
   */
  launchScheduled(campId, schedule: string, timezone: string = '0', code, reLaunch = false, syncInfluencersBeforeLaunch?: boolean, syncHoursBeforeLaunch?: number, requireSuccessfulSync?: boolean, syncRegistrationPlatform?: string,
    syncOptions: LaunchSyncOptions = {}) {
    return this.http.post(`${this.campaignUrl}/${campId}/launch/scheduled`, {
      schedule,
      timezone,
      launch_code: code,
      ...(reLaunch ? {reLaunch} : {}),
      ...(syncInfluencersBeforeLaunch !== undefined ? {sync_influencers_before_launch: syncInfluencersBeforeLaunch} : {}),
      ...(syncHoursBeforeLaunch !== undefined ? {sync_hours_before_launch: syncHoursBeforeLaunch} : {}),
      ...(requireSuccessfulSync !== undefined ? {require_successful_sync: requireSuccessfulSync} : {}),
      ...(syncRegistrationPlatform ? {sync_registration_platform: syncRegistrationPlatform} : {}),
      ...(syncOptions.sync_project_registration_platform_id ? {sync_project_registration_platform_id: syncOptions.sync_project_registration_platform_id} : {}),
      ...(syncOptions.sync_influencers_after_launch !== undefined ? {sync_influencers_after_launch: !!syncOptions.sync_influencers_after_launch} : {}),
      ...(syncOptions.sync_influencers_after_launch && syncOptions.sync_after_launch_registration_platform_id
        ? {sync_after_launch_registration_platform_id: syncOptions.sync_after_launch_registration_platform_id} : {})
    });
  }

  deleteCampaign(campId: number) {
    return this.http.delete(`${this.campaignUrl}/${campId}`);
  }

  copyCampaign(campId: number, body: any) {
    return this.http.post(`${this.campaignUrl}/${campId}/copy`, body);
  }

  cancelCampaignLaunch(campId: number) {
    return this.http.post(`${this.campaignUrl}/${campId}/launch/cancel`, {});
  }

  campaignScheduleList(params) {
    return this.http.get(`${this.campaignUrl}/overview`, {params});
  }

  getLaunchHistory(campId: number) {
    return this.http.get(`${this.campaignUrl}/${campId}/launch/history`);
  }

  widgetPreviewUrl(id: number, genericShare?: boolean, influencerId?: number) {
    return this.http.post(`${this.shareUrl}/${id}/preview`, {genericShare, influencerId});
  }
}
